import { useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip } from "react-leaflet";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import useHome from "@/lib/useHome";
import { useFilters } from "@/context/FiltersContext";
import { setPreference } from "@/lib/choices";
import { toast } from "sonner";
import { Loader2, Sparkles, Route, Trash2, ChevronUp, ChevronDown, Navigation, Crown, MapPin, Home as HomeIcon, ExternalLink, BookmarkCheck } from "lucide-react";

const DEFAULT_RULES =
  "Canadian circuit — must hit the Ponoka Stampede. Target 40+ rodeos within my events, " +
  "maximize added money, and keep drives reasonable. Favour stops that build toward CFR qualification.";

const CFR_RULES =
  "CFR 2026 QUALIFYING (CPRA) — Canadian Finals Rodeo, Oct 7–10 2026, Rogers Place, Edmonton.\n" +
  "• Qualify by CPRA season MONEY standings; the top 12 per event make the CFR (association takes up to top 15). Season ends Labour Day weekend (Sep 7, 2026).\n" +
  "• Must be a full-carded CPRA member — permit / semi-pro earnings don't count.\n" +
  "• Minimum season rodeo counts: 18 (ladies barrel), 15 (breakaway), 15 (all other events).\n" +
  "• Count rules: rough stock (BB/SB/BR) ≥10 CPRA rodeos (5 first-approved); timed events (SW/TD/breakaway/barrel) ≥10 (7 first-approved); team roping ≥15 (8 first-approved).\n" +
  "GOAL: build a CPRA route from now through Labour Day that hits the minimum rodeo count and maximizes money won — prioritise high-added-money CPRA rodeos (e.g. Ponoka, Calgary, Medicine Hat) while keeping drives reasonable.";

const PRESETS = {
  custom: { label: "Custom", rules: DEFAULT_RULES, assoc: "", dateTo: "" },
  cfr: { label: "CFR — Canadian Finals (CPRA)", rules: CFR_RULES, assoc: "CPRA", dateTo: "" },
};

const numIcon = (n, home = false) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${home ? "#E68A2E" : "#C8000F"};color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:12px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${home ? "⌂" : n}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });

export default function RouteMapper() {
  const m = useIsMobile();
  const home = useHome();
  const { events: eventSet } = useFilters();

  const [eventsText, setEventsText] = useState([...(eventSet || [])].join(", "));
  const [association, setAssociation] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [mustAttend, setMustAttend] = useState("");
  const [maxDrive, setMaxDrive] = useState("");
  const [preset, setPreset] = useState("custom");
  const [rulesText, setRulesText] = useState(DEFAULT_RULES);

  const applyPreset = (key) => {
    setPreset(key);
    const p = PRESETS[key];
    if (!p) return;
    setRulesText(p.rules);
    setAssociation(p.assoc);
    setDateTo(p.dateTo);
  };

  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [stops, setStops] = useState([]);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "min-h-screen bg-[#F4F6F8]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";
  const field = m
    ? "w-full bg-white/5 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F]"
    : "w-full bg-white border border-[#D7DBE0] text-slate-900 placeholder-slate-400 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F]";
  const label = `text-[11px] font-bold uppercase tracking-wide ${sub} mb-1 block`;

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/route-mapper/generate", {
        events: eventsText.split(",").map((s) => s.trim()).filter(Boolean),
        association,
        date_from: dateFrom,
        date_to: dateTo,
        must_attend: mustAttend.split(",").map((s) => s.trim()).filter(Boolean),
        max_drive_km: maxDrive ? Number(maxDrive) : null,
        rules_text: rulesText,
        home: { town: home.town, lat: home.lat, lng: home.lng },
      });
      setPlan(data);
      setStops(data.stops_full || []);
      if (!data.stops_full?.length) toast.message(data.error ? "Planner had trouble — try again" : "No stops matched — widen your dates or rules");
      else toast.success(`Drafted a ${data.stops_full.length}-stop route from ${data.candidate_count} rodeos`);
    } catch (e) {
      toast.error("Couldn't build the route. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const removeStop = (i) => setStops((s) => s.filter((_, idx) => idx !== i));
  const moveStop = (i, dir) => setStops((s) => {
    const n = [...s]; const j = i + dir;
    if (j < 0 || j >= n.length) return n;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const totalMoney = stops.reduce((a, s) => a + (s.added_money || 0), 0);
  const totalKm = stops.reduce((a, s) => a + (s.distance_km || 0), 0);
  const line = stops.filter((s) => s.lat && s.lng).map((s) => [s.lat, s.lng]);
  const homePt = home.lat ? [home.lat, home.lng] : null;
  const poly = homePt ? [homePt, ...line] : line;

  const saveRoute = () => {
    try { localStorage.setItem("rp_route_plan", JSON.stringify({ stops, rulesText, savedAt: Date.now() })); toast.success("Route saved to this device"); }
    catch { toast.error("Couldn't save"); }
  };

  const addAllToMyRodeos = () => {
    let n = 0;
    stops.forEach((s) => { if (s.perf1_id) { setPreference(s.rodeo_id, s.perf1_id, 1); n += 1; } });
    toast.success(n ? `Added ${n} rodeo${n > 1 ? "s" : ""} to My Rodeos` : "No performances to add yet");
  };

  const openAllEntries = () => {
    const links = stops.map((s) => s.entry_link).filter(Boolean);
    if (!links.length) return toast.error("No entry links available");
    links.forEach((u, i) => setTimeout(() => window.open(u, "_blank", "noopener"), i * 400));
    toast.message(`Opening ${links.length} entry page${links.length > 1 ? "s" : ""} — allow pop-ups if blocked`);
  };

  return (
    <div className={page} data-testid="route-mapper-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <Route className="w-6 h-6 text-[#C8000F]" />
        <div>
          <h1 className={`font-display text-xl font-extrabold flex items-center gap-2 ${title}`}>
            AI Route Mapper
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#E68A2E]/20 text-[#E68A2E]"><Crown className="w-3 h-3" /> PRO</span>
          </h1>
          <div className={`text-xs ${sub}`}>Draft a season schedule from your rules — then tweak it into the perfect run</div>
        </div>
      </div>

      <div className="p-4 sm:p-6 grid lg:grid-cols-[380px_1fr] gap-5 max-w-6xl">
        {/* ---- Form ---- */}
        <div className={`rounded-xl border ${card} p-4 h-fit`} data-testid="route-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={label}>Goal preset</label>
              <select value={preset} onChange={(e) => applyPreset(e.target.value)} className={field} data-testid="rm-preset">
                {Object.entries(PRESETS).map(([k, p]) => <option key={k} value={k} className="bg-[#0C1017] lg:bg-white">{p.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={label}>My events</label>
              <input value={eventsText} onChange={(e) => setEventsText(e.target.value)} placeholder="e.g. Bull Riding, Team Roping" className={field} data-testid="rm-events" />
            </div>
            <div>
              <label className={label}>Circuit / Assoc.</label>
              <input value={association} onChange={(e) => setAssociation(e.target.value)} placeholder="any" className={field} data-testid="rm-assoc" />
            </div>
            <div>
              <label className={label}>Max drive/day (km)</label>
              <input value={maxDrive} onChange={(e) => setMaxDrive(e.target.value)} placeholder="optional" className={field} data-testid="rm-maxdrive" />
            </div>
            <div>
              <label className={label}>From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={field} data-testid="rm-from" />
            </div>
            <div>
              <label className={label}>To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={field} data-testid="rm-to" />
            </div>
            <div className="col-span-2">
              <label className={label}>Must-attend rodeos</label>
              <input value={mustAttend} onChange={(e) => setMustAttend(e.target.value)} placeholder="e.g. Ponoka, Calgary" className={field} data-testid="rm-must" />
            </div>
            <div className="col-span-2">
              <label className={label}>Qualifying rules & goal</label>
              <textarea value={rulesText} onChange={(e) => setRulesText(e.target.value)} rows={5} className={field} data-testid="rm-rules" />
              <p className={`text-[11px] mt-1 ${sub}`}>Edit this in plain English — the planner reads it and you can regenerate anytime.</p>
            </div>
          </div>
          <div className={`flex items-center gap-2 mt-3 text-[12px] ${sub}`}>
            <HomeIcon className="w-3.5 h-3.5" /> Home: {home.town || (home.isDefault ? "default point (set in profile)" : "set")}
          </div>
          <button onClick={generate} disabled={loading} data-testid="rm-generate"
            className="w-full mt-3 flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] disabled:opacity-60 text-white font-semibold py-3 rounded-xl">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Building your route…</> : <><Sparkles className="w-4 h-4" /> Build my route</>}
          </button>
        </div>

        {/* ---- Result ---- */}
        <div className="min-w-0">
          {!plan ? (
            <div className={`rounded-xl border border-dashed p-10 text-center ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`} data-testid="rm-empty">
              <Route className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <div className={`font-display font-bold ${title}`}>Your draft route will appear here</div>
              <div className="text-sm mt-1">Set your events, dates and rules, then hit <b>Build my route</b>. You'll get an ordered schedule you can reorder, trim and save.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {plan.summary && (
                <div className={`rounded-xl border ${card} p-4`} data-testid="rm-summary">
                  <div className={`text-[11px] font-bold uppercase tracking-wide ${sub} mb-1`}>Plan summary</div>
                  <p className={`text-sm leading-relaxed ${m ? "text-slate-300" : "text-slate-700"}`}>{plan.summary}</p>
                </div>
              )}

              {stops.length > 0 && (
                <>
                  <div className={`rounded-xl border ${card} px-4 py-3 flex flex-wrap items-center justify-between gap-3`} data-testid="rm-tally">
                    <div className={`text-sm ${sub}`}><b className={title}>{stops.length}</b> rodeos · <b className={title}>{totalKm.toLocaleString()} km</b> total haul</div>
                    <div className={`font-display font-bold ${title}`}>{money(totalMoney)} added</div>
                    <div className="flex items-center gap-2">
                      <button onClick={addAllToMyRodeos} data-testid="rm-add-all" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 lg:bg-[#F1F3F5] text-[#C8000F] px-3 py-1.5 rounded-lg hover:opacity-90"><BookmarkCheck className="w-3.5 h-3.5" /> Add all to My Rodeos</button>
                      <button onClick={openAllEntries} data-testid="rm-open-all" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 lg:bg-[#F1F3F5] text-slate-200 lg:text-slate-700 px-3 py-1.5 rounded-lg hover:opacity-90"><ExternalLink className="w-3.5 h-3.5" /> Open entry pages</button>
                      <button onClick={saveRoute} data-testid="rm-save" className="text-xs font-semibold bg-[#C8000F] text-white px-3 py-1.5 rounded-lg">Save route</button>
                    </div>
                  </div>

                  {poly.length > 1 && (
                    <div className={`rounded-xl overflow-hidden border ${card}`} style={{ height: 300 }} data-testid="rm-map">
                      <MapContainer center={line[0] || homePt || [52, -108]} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                        {homePt && <Marker position={homePt} icon={numIcon(0, true)}><Tooltip>Home</Tooltip></Marker>}
                        {stops.map((s, i) => s.lat && s.lng && (
                          <Marker key={s.rodeo_id} position={[s.lat, s.lng]} icon={numIcon(i + 1)}><Tooltip>{i + 1}. {s.name}</Tooltip></Marker>
                        ))}
                        <Polyline positions={poly} color="#C8000F" weight={2} dashArray="5 6" />
                      </MapContainer>
                    </div>
                  )}

                  <div className="space-y-2" data-testid="rm-stops">
                    {stops.map((s, i) => (
                      <div key={s.rodeo_id} className={`rounded-xl border ${card} p-3 flex items-start gap-3`} data-testid={`rm-stop-${s.rodeo_id}`}>
                        <div className="w-8 h-8 rounded-full bg-[#C8000F] text-white font-display font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                        <div className="min-w-0 flex-1">
                          <div className={`font-display font-bold ${title}`}>{s.name}</div>
                          <div className={`text-xs ${sub}`}>{[s.city, s.state].filter(Boolean).join(", ")} · {fmtDate(s.date)}{s.distance_km ? ` · ${s.distance_km.toLocaleString()} km from home` : ""}</div>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {s.added_money ? <span className="text-[11px] font-semibold text-[#E68A2E]">{money(s.added_money)} added</span> : null}
                            {s.entry_close ? <span className={`text-[11px] ${sub}`}>EC {fmtDate(s.entry_close)}</span> : null}
                            {s.entry_link ? <a href={s.entry_link} target="_blank" rel="noreferrer" data-testid={`rm-enter-${s.rodeo_id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C8000F] hover:underline"><ExternalLink className="w-3 h-3" /> Enter</a> : null}
                          </div>
                          {s.reason && <p className={`text-[12px] mt-1.5 ${m ? "text-slate-400" : "text-slate-600"}`}><span className="font-semibold text-[#C8000F]">Why:</span> {s.reason}</p>}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button onClick={() => moveStop(i, -1)} disabled={i === 0} data-testid={`rm-up-${s.rodeo_id}`} className={`p-1 rounded ${sub} disabled:opacity-30 hover:text-[#C8000F]`}><ChevronUp className="w-4 h-4" /></button>
                          <button onClick={() => moveStop(i, 1)} disabled={i === stops.length - 1} data-testid={`rm-down-${s.rodeo_id}`} className={`p-1 rounded ${sub} disabled:opacity-30 hover:text-[#C8000F]`}><ChevronDown className="w-4 h-4" /></button>
                          <button onClick={() => removeStop(i)} data-testid={`rm-remove-${s.rodeo_id}`} className="p-1 rounded text-slate-400 hover:text-[#C8000F]"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {(plan.notes || []).length > 0 && (
                <div className={`rounded-xl border ${card} p-4`} data-testid="rm-notes">
                  <div className={`text-[11px] font-bold uppercase tracking-wide ${sub} mb-1.5`}>Planner notes</div>
                  <ul className="space-y-1">
                    {plan.notes.map((n, i) => <li key={i} className={`text-[12px] flex gap-2 ${m ? "text-slate-400" : "text-slate-600"}`}><span className="text-[#E68A2E]">•</span> {n}</li>)}
                  </ul>
                </div>
              )}

              <p className={`text-[11px] ${sub}`}>Scaffold preview — the planner drafts from live rodeo data using the rules you type. Encode exact CFR/circuit qualification rules in the rules box for sharper results.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
